if (!customElements.get('header-search')) {
  class HeaderSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');

      if (!this.input || !this.form) return;

      this.panel = document.getElementById(this.input.getAttribute('aria-controls'));
      this.header = this.closest('.site-header');

      if (!this.panel || !this.header || this.dataset.enabled === 'false') return;

      this.matches = this.panel.querySelector('[data-header-search-matches]');
      this.status = this.panel.querySelector('[data-header-search-status]');
      this.allResults = this.panel.querySelector('[data-header-search-all]');

      this.cache = new Map();
      this.requestId = 0;

      this.setupEvents();

      this.observer = new ResizeObserver(() => this.positionPanel());
      this.observer.observe(this.header);
    }

    disconnectedCallback() {
      this.close();
      this.events?.abort();
      this.observer?.disconnect();
    }

    setupEvents() {
      this.events = new AbortController();
      const options = { signal: this.events.signal };

      const closeOutside = (event) => {
        if (!this.contains(event.target) && !this.panel.contains(event.target)) {
          this.close();
        }
      };

      this.input.addEventListener('input', () => this.onInput(), options);

      this.input.addEventListener('focus', () => {
        if (!this.suppressFocus && this.input.value.trim()) {
          this.onInput();
        }
      }, options);

      document.addEventListener('pointerdown', closeOutside, options);
      document.addEventListener('focusin', closeOutside, options);

      document.addEventListener('keydown', event => this.onKeydown(event), {
        ...options,
        capture: true
      });

      document.addEventListener('predictive-search:open', () => this.close(), options);

      window.addEventListener('resize', () => {
        if (window.innerWidth <= 900) {
          this.close();
        } else {
          this.positionPanel();
        }
      }, options);

      window.addEventListener('scroll', () => {
        if (!this.panel.hidden) {
          this.positionPanel();
        }
      }, { ...options, passive: true });
    }

    onInput() {
      clearTimeout(this.timer);
      this.request?.abort();

      const requestId = ++this.requestId;
      const term = this.input.value.trim();

      if (!term) {
        this.matches.innerHTML = '';
        this.close();
        return;
      }

      const url = new URL(this.form.action);
      url.searchParams.set('q', term);
      url.searchParams.set('options[prefix]', 'last');

      this.allResults.href = url.href;
      this.allResults.textContent = `Show all results for ‘${term}’`;

      this.matches.innerHTML = '';
      this.status.textContent = 'Searching…';
      this.matches.setAttribute('aria-busy', 'true');

      this.open();

      if (this.cache.has(term)) {
        this.render(this.cache.get(term));
        return;
      }

      this.timer = setTimeout(() => {
        this.getResults(term, requestId);
      }, 250);
    }

    async getResults(term, requestId) {
      this.request = new AbortController();

      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        section_id: 'header-search-results'
      });

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, {
          signal: this.request.signal
        });

        if (!response.ok) {
          throw new Error('Search request failed');
        }

        const text = await response.text();

        if (requestId !== this.requestId) return;

        const parser = new DOMParser();
        const page = parser.parseFromString(text, 'text/html');
        const section = page.querySelector('#shopify-section-header-search-results');

        if (!section) {
          throw new Error('Search results missing');
        }

        if (this.cache.size >= 10) {
          const oldestTerm = this.cache.keys().next().value;
          this.cache.delete(oldestTerm);
        }

        this.cache.set(term, section.innerHTML);
        this.render(section.innerHTML);

      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.requestId) return;

        this.matches.removeAttribute('aria-busy');
        this.status.textContent = 'Suggestions are unavailable. View all results below.';
      }
    }

    render(markup) {
      this.matches.innerHTML = markup;
      this.matches.removeAttribute('aria-busy');

      const productCount = this.matches.querySelectorAll('.header-search-panel__product').length;

      this.status.textContent = `${productCount} product ${productCount === 1 ? 'match' : 'matches'}.`;
      this.status.classList.add('visually-hidden');
    }

    positionPanel() {
      if (!this.panel || this.panel.hidden) return;

      const headerRect = this.header.getBoundingClientRect();
      const layoutRect = this.header.querySelector('.header-layout').getBoundingClientRect();

      const panelTop = layoutRect.bottom - headerRect.top;
      const panelHeight = Math.max(100, window.innerHeight - layoutRect.bottom - 20);

      this.panel.style.setProperty('--header-search-top', `${panelTop}px`);
      this.panel.style.setProperty('--header-search-height', `${panelHeight}px`);
    }

    open() {
      this.status.classList.remove('visually-hidden');
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');

      this.positionPanel();
    }

    close() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.requestId++;

      if (this.panel) {
        this.panel.hidden = true;
      }

      this.input?.setAttribute('aria-expanded', 'false');
    }

    onKeydown(event) {
      if (this.panel.hidden) return;

      const isInsideSearch = this.contains(event.target) || this.panel.contains(event.target);

      if (!isInsideSearch) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();

        this.close();

        this.suppressFocus = true;
        this.input.focus();
        this.suppressFocus = false;

        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const links = Array.from(this.panel.querySelectorAll('a[href]'));
      const index = links.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;

      event.preventDefault();

      if (next < 0) {
        this.input.focus();
      } else {
        const nextLinkIndex = Math.min(next, links.length - 1);
        const nextLink = links[nextLinkIndex];

        nextLink?.focus();
      }
    }
  }

  customElements.define('header-search', HeaderSearch);
}
